import { NgClass } from '@angular/common';
import { Component, computed, DestroyRef, effect, ElementRef, inject, input, NgZone, OnInit, output, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ControlContainer,
    ControlValueAccessor,
    FormControl,
    FormControlDirective,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    StatusChangeEvent
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

import { OwlDateTimeModule, PickerType } from '@danielmoncada/angular-datetime-picker';
import { filter, Subscription } from 'rxjs';

import { BaasI18n } from '@lvflanders/bar';

import { BAR_DATE_TIME_PROVIDERS } from '../../controls/bar-controls.providers';
import { BAAS_DATE_TIME_PICKER_PROVIDER, BaasDateTimePickerProvider } from './baas-date-time-picker.provider';

export type BarDateTimePickerType = 'dateTime' | 'date' | 'time';
export type BarDateTimePickerRangeSelectMode = 'default' | 'from' | 'to';
export type BarDateTimePickerYearAdjustment = 'none' | 'default' | 'fixedCentury' | 'rollingCentury';

type UpdateOn = 'change' | 'blur' | 'submit';
// TODO refactor this to be less FormControl centric (ideally when we change to another date & time library/system)
@Component({
    selector: 'baas-date-time-picker',
    templateUrl: './baas-date-time-picker.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: BaasDateTimePicker,
            multi: true
        },
        [...BAR_DATE_TIME_PROVIDERS]
    ],
    imports: [NgClass, FormsModule, ReactiveFormsModule, OwlDateTimeModule, MatIconModule, MatFormFieldModule, MatInputModule, MatTooltipModule]
})
export class BaasDateTimePicker implements ControlValueAccessor, OnInit {
    readonly #controlContainer = inject(ControlContainer, { optional: true });
    readonly #ngZone = inject(NgZone);
    readonly #i18n = inject(BaasI18n);
    readonly #destroyRef = inject(DestroyRef);
    readonly #provider = inject<BaasDateTimePickerProvider>(BAAS_DATE_TIME_PICKER_PROVIDER);

    readonly input = viewChild.required<ElementRef<HTMLInputElement>>('input');
    readonly formControlDirective = viewChild.required(FormControlDirective);

    readonly formControl = input<FormControl>();
    readonly formControlName = input<string>();
    readonly value = input<any>();
    /** Id used to associate with label for. */
    readonly labelForId = input<string>();
    /** Label text. If left out no label is used. */
    readonly label = input<string>();
    /** Wether to wrap the label over multiple lines. */
    readonly wrapLabel = input<boolean>(false);
    /** Tooltip for the label. */
    readonly labelTooltip = input<string>();
    /** DateTime picker type. */
    readonly _pickerType = input<BarDateTimePickerType>('date', { alias: 'pickerType' });
    /** Min selectable date. */
    readonly min = input<Date | undefined>();
    /** Max selectable date. */
    readonly max = input<Date | undefined>();
    /** Start date at which to start the calendar. */
    readonly startAt = input<Date | undefined>();
    /** Whether a range should be selected. */
    readonly range = input(false);
    /** Which kind of range select mode in case range is true. */
    readonly rangeSelectMode = input<BarDateTimePickerRangeSelectMode>('default');
    readonly readonly = input(false);
    readonly required = input<boolean | undefined>(undefined);
    readonly includeSeconds = input(false);
    /** Allows to filter specific dates. */
    readonly dateTimeFilter = input<(date: Date) => boolean>();
    /** Ensures that the input textbox always contains a valid value on blur. If the form control value is null the input is emptied. */
    readonly ensureValidInput = input(true);
    /** @experimental Automatically adjusts the year in case less than 3 digits. */
    readonly yearAdjustment = input<BarDateTimePickerYearAdjustment | boolean>(undefined);
    /** Shortcut callback allowing to set a given Date when a keyboard event occurs. */
    readonly shortcutHandler = input<(event: KeyboardEvent) => Date | undefined | null>();
    /**
     * Defines when the value is updated when value entered through input with ngModel.
     * Note does not work with formControl or formControlName. In this case you need to use the FormControl options updateOn.
     */
    readonly updateOn = input<UpdateOn>('change');
    /** Occurs on input blur. */
    readonly blur = output();
    /** Occurs when a date is selected within the calendar. */
    readonly selected = output<any>();
    /** Occurs when the date changes within the picker (through input or calendar) */
    readonly changed = output<any>();

    protected readonly pickerType = computed<PickerType>(() => {
        switch (this._pickerType()) {
            case 'dateTime':
                return 'both';
            case 'time':
                return 'timer';
            default:
                return 'calendar';
        }
    });
    protected readonly selectionTitle = computed(() => {
        switch (this._pickerType()) {
            case 'dateTime':
                return this.#i18n.get('DOMAIN_BAR_CONTROLS.DATE_TIME_PICKER.SELECTION_TITLE.DATE_TIME');
            case 'time':
                return this.#i18n.get('DOMAIN_BAR_CONTROLS.DATE_TIME_PICKER.SELECTION_TITLE.TIME');
            default:
                return this.#i18n.get('DOMAIN_BAR_CONTROLS.DATE_TIME_PICKER.SELECTION_TITLE.DATE');
        }
    });
    protected readonly selectMode = computed(() => {
        if (!this.range()) return 'single';
        else {
            switch (this.rangeSelectMode()) {
                case 'from':
                    return 'rangeFrom';
                case 'to':
                    return 'rangeTo';
                default:
                    return 'range';
            }
        }
    });

    protected readonly disabled = computed(() => this.#formControlDisabled() || this.readonly());

    readonly #formControlDisabled = signal<boolean>(undefined);

    readonly #yearAdjustment = computed(() => {
        switch (this.yearAdjustment()) {
            case 'default':
            case 'rollingCentury':
            case true:
                return 'rollingCentury';
            case false:
                return 'none';
            case 'fixedCentury':
                return 'fixedCentury';
            default:
                return 'none';
        }
    });

    // NOTE we must always have a form control
    #defaultFormControl: FormControl;
    protected readonly control = signal<FormControl>(undefined);

    #formControlEventsSubscription: Subscription;
    #formControlValueChangesSubscription: Subscription;

    #onChange: (value: any) => void = () => {};
    #onTouch: () => void = () => {};
    constructor() {
        effect(() => {
            if (this.value()) this.#defaultFormControl.setValue(this.value());
        });
        effect(() => {
            // unsubscribe in case we already initialized a valueChanges subscription (could happen in theory in case the consumer changes the formControl during the lifecycle of the component)
            if (this.#formControlValueChangesSubscription) this.#formControlValueChangesSubscription.unsubscribe();

            if (this.formControl()) this.control.set(this.formControl());
            else if (this.#controlContainer && this.formControlName())
                this.control.set(<FormControl>this.#controlContainer.control.get(this.formControlName()));
            else this.control.set(this.#defaultFormControl);

            this.#formControlValueChangesSubscription = this.control()
                .valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
                .subscribe((value) => {
                    this.changed.emit(value);
                });

            const syncFormControlDisabled = () => this.#formControlDisabled.set(this.control().disabled);

            syncFormControlDisabled();

            // NOTE unsubscribe in case the formControl would change while the date time picker is still alive
            if (this.#formControlEventsSubscription) this.#formControlEventsSubscription.unsubscribe();

            this.#formControlEventsSubscription = this.control()
                .events.pipe(
                    filter((event) => event instanceof StatusChangeEvent),
                    takeUntilDestroyed(this.#destroyRef)
                )
                .subscribe((value) => untracked(() => syncFormControlDisabled()));
        });
    }

    ngOnInit(): void {
        // Q&D we assign stuff here without signals and effects because it was messing up (ngModel context)
        // next one is used in case ngModel is used, here we check if there is an updateOn option (faker)
        this.#defaultFormControl = new FormControl(undefined, { updateOn: this.updateOn() || 'change' });
        // we assign to the control which is bound in the template (there is also an effect setting this)
        this.control.set(this.#defaultFormControl);
        // NOTE used in case ngModel is defined
        // Subscribe to control value changes and propagate them to the parent form/ngModel
        // because the default form control does not propage changes back to consumer
        this.#defaultFormControl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
            if (this.#onChange) this.#onChange(value);
            if (this.#onTouch) this.#onTouch();
        });
    }

    protected onKeyDown(event: KeyboardEvent): void {
        let result: Date = undefined;
        if (this.shortcutHandler()) {
            result = this.shortcutHandler()(event);
        } else if (this.#provider.handleShortcut) {
            result = this.#provider.handleShortcut(event);
        }
        if (result !== undefined) {
            this.#ngZone.runOutsideAngular(() => {
                // NOTE fix value was always empties on blur in case not debugging
                window.setTimeout(() => this.control().setValue(result, { emitEvent: true }));
            });
        }
    }

    onDateSelected(date: any) {
        this.selected.emit(date);
    }

    protected onBlur(): void {
        if (
            this.ensureValidInput() &&
            this.control() &&
            this.control().value == null &&
            this.input()?.nativeElement?.value &&
            this.input().nativeElement.value.length > 0
        ) {
            this.input().nativeElement.value = null;
        } else if (this.#yearAdjustment() !== 'none') {
            const date = this.control().value as Date;
            if (date instanceof Date && date.getFullYear() < 100) {
                const adjustedYear = this.#adjustYear(date.getFullYear());
                this.control().setValue(new Date(date.setFullYear(adjustedYear)), { emitEvent: true });
            }
        }

        this.blur.emit();
    }

    registerOnTouched(fn: any): void {
        this.#onTouch = fn;
        this.formControlDirective().valueAccessor.registerOnTouched(fn);
    }

    registerOnChange(fn: any): void {
        this.#onChange = fn;
        this.formControlDirective().valueAccessor.registerOnChange(fn);
    }

    writeValue(obj: any): void {
        this.#ngZone.runOutsideAngular(() => {
            window.setTimeout(() => {
                this.#ngZone.run(() => {
                    this.formControlDirective().valueAccessor.writeValue(obj);
                });
            });
        });
    }

    setDisabledState(isDisabled: boolean): void {
        this.formControlDirective().valueAccessor.setDisabledState(isDisabled);
    }

    focus(): void {
        this.input()?.nativeElement?.focus();
    }

    #adjustYear(year: number): number {
        if (year < 100) {
            const currentYear = new Date().getFullYear();
            const currentCentury = Math.floor(currentYear / 100) * 100;

            switch (this.#yearAdjustment()) {
                case 'rollingCentury': {
                    const lastTwoDigits = currentYear % 100; // Extract last two digits of the current year
                    if (year < 50) {
                        return lastTwoDigits < 50 ? currentCentury + year : currentCentury + 100 + year;
                    } else {
                        return lastTwoDigits < 50 ? currentCentury - 100 + year : currentCentury + year;
                    }
                }
                case 'fixedCentury':
                    return currentCentury + year;
            }
        }
        return year;
    }
}
